import { ActionModal } from '@kcftech/design-system';
import { IAssetCategoryResponse } from '@kcftech/prometheustypescriptapiclient';
import { useMutation } from '@tanstack/react-query';
import { useNotificationContext } from 'contexts/NotificationContext';
import {
    AssetCategoryFormContent,
    AssetCategoryFormData,
} from 'pages/Settings/AssetType/AssetCategoryFormContent';
import { useForm } from 'react-hook-form';
import { updateAssetCategory } from 'utils/asset_classification';

export type CategoryEditInfo = Readonly<{
    category: IAssetCategoryResponse;
    siblingShortNames: string[];
}>;

export type EditAssetCategoryProps = CategoryEditInfo &
    Readonly<{
        onClose: (updatedCategory: IAssetCategoryResponse | undefined) => void;
    }>;

export function EditAssetCategoryModal({
    category,
    siblingShortNames,
    onClose,
}: EditAssetCategoryProps) {
    const { showToast } = useNotificationContext();
    const form = useForm<AssetCategoryFormData>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            name: category.ShortName,
        },
    });

    const {
        handleSubmit,
        formState: { isSubmitted, isValid, isValidating, isSubmitting },
    } = form;

    const { mutate: updateCategory, isPending } = useMutation({
        mutationFn: async (data: AssetCategoryFormData) =>
            updateAssetCategory(category.Id, { ShortName: data.name.trim() }),

        onSuccess: (result) => {
            onClose(result);
            showToast({
                type: 'success',
                content: 'Successfully updated asset category',
            });
        },

        onError: () =>
            showToast({ type: 'error', content: 'Failed to updated asset category' }),
    });

    const onUpdate = handleSubmit((formData) => updateCategory(formData));

    return (
        <ActionModal
            onAction={onUpdate}
            actionable={
                (!isSubmitted || isValid) && !(isValidating || isSubmitting || isPending)
            }
            onClose={() => onClose(undefined)}
            title="Edit Asset Category"
            height={350}
            actionButtonText="Save"
            closeButtonText="Cancel"
        >
            <AssetCategoryFormContent
                form={form}
                siblingShortNames={siblingShortNames}
                parentCategoryName={category.ParentCategory?.AssetCategoryParentName}
            />
        </ActionModal>
    );
}
